import type { PriceTagScan, ReceiptSummary } from '@recipes/contracts';
import { Alert, Badge, Button, Spinner } from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ReceiptText, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useCatalogBoot } from '@/features/catalog/hooks';
import { formatMoney, usePricesEnabled } from '@/features/prices/hooks';
import { PriceDialog } from '@/features/prices/price-dialog';
import {
  discardTripPhoto,
  replayTripPhotos,
  usePendingTripPhotos,
} from '@/features/prices/trip-photos';
import { useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeWorkerError, toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { currentFormatLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

const receiptKeys = { ...queryKeys.receipts, inbox: queryKeys.prices.inbox };

const dateTime = (value: string) =>
  new Intl.DateTimeFormat(currentFormatLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value));

function ReceiptRow({ receipt }: { receipt: ReceiptSummary }) {
  const { t } = useTranslation();
  return (
    <li data-testid="receipt-row" data-status={receipt.status}>
      <Link
        to={`/shopping/receipts/${receipt.id}`}
        className="flex min-h-[56px] items-center justify-between gap-3 px-3 py-2"
      >
        <span className="min-w-0">
          <span className="block truncate font-medium">
            {receipt.store?.name ?? receipt.storeNameRaw ?? t('receipts.unknownStore')}
          </span>
          <span className="block text-xs text-muted-foreground">
            {/* A receipt prints local time without a zone; it is shown as printed. */}
            {receipt.purchasedAt ? dateTime(receipt.purchasedAt) : dateTime(receipt.createdAt)}
            {receipt.status !== 'PROCESSING' &&
              ` · ${t('receipts.matched', {
                matched: receipt.matchedLineCount,
                total: receipt.lineCount,
              })}`}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          {receipt.total && receipt.currency && (
            <span className="tabular-nums">{formatMoney(receipt.total, receipt.currency)}</span>
          )}
          <Badge variant={receipt.status === 'CONFIRMED' ? 'muted' : 'secondary'}>
            {t(`receipts.status.${receipt.status}`)}
          </Badge>
        </span>
      </Link>
    </li>
  );
}

/**
 * Receipts and the price inbox (docs/partial/shopping-in-store-spec.md 5.4, 6.4): the trips of
 * the household, photos that still wait for a connection, and price tags that were read and
 * wait for the user's Save.
 */
export function ReceiptsPage() {
  const { t } = useTranslation();
  useCatalogBoot();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const pricesEnabled = usePricesEnabled();
  const receiptsEnabled = useFeature('receipt_scan');
  const tagsEnabled = useFeature('price_tag_scan');
  const pending = usePendingTripPhotos();
  const [confirming, setConfirming] = useState<PriceTagScan | null>(null);

  const receipts = useQuery({
    queryKey: receiptKeys.all,
    queryFn: () => api.receipts.list({ limit: 50 }),
    enabled: online && receiptsEnabled,
    refetchInterval: (query) =>
      query.state.data?.items.some((receipt) => receipt.status === 'PROCESSING') ? 3000 : false,
  });
  const inbox = useQuery({
    queryKey: receiptKeys.inbox,
    queryFn: () => api.prices.inbox(),
    enabled: online && tagsEnabled,
    refetchInterval: (query) =>
      query.state.data?.items.some((scan) => scan.status === 'PROCESSING') ? 2000 : false,
  });
  const dismiss = useMutation({
    mutationFn: (scanId: string) => api.prices.dismissScan(scanId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: receiptKeys.inbox }),
    onError: (e) => toastError(e),
  });

  // Photos taken without a connection are sent as soon as there is one.
  useEffect(() => {
    if (!online || pending.length === 0) return;
    void replayTripPhotos().then((sent) => {
      if (sent === 0) return;
      void queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      void queryClient.invalidateQueries({ queryKey: receiptKeys.inbox });
    });
  }, [online, pending.length, queryClient]);

  if (!pricesEnabled)
    return (
      <PageContainer>
        <Alert>{t('prices.store.disabled')}</Alert>
      </PageContainer>
    );

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('receipts.title')}
        description={t('receipts.description')}
        backTo="/shopping"
        actions={
          receiptsEnabled && (
            <Button asChild>
              <Link to="/shopping/receipts/new" data-testid="receipt-new">
                <Camera aria-hidden /> {t('receipts.scan')}
              </Link>
            </Button>
          )
        }
      />
      {!online && <Alert className="mb-4">{t('receipts.offline')}</Alert>}

      {pending.length > 0 && (
        <section
          className="mb-6"
          aria-labelledby="receipts-waiting"
          data-testid="trip-photos-waiting"
        >
          <h2 id="receipts-waiting" className="mb-2 text-lg font-semibold">
            {t('receipts.waiting.title', { count: pending.length })}
          </h2>
          <ul className="divide-y rounded-lg border text-sm">
            {pending.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-2 p-3">
                <span>
                  {row.kind === 'receipt'
                    ? t('receipts.waiting.receipt', { count: row.photos.length })
                    : t('receipts.waiting.tag')}
                  {row.lastError && (
                    <span className="block text-xs text-destructive">{row.lastError}</span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('receipts.waiting.discard')}
                  onClick={() => void discardTripPhoto(row.id)}
                >
                  <Trash2 aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tagsEnabled && (inbox.data?.items.length ?? 0) > 0 && (
        <section className="mb-6" aria-labelledby="price-inbox" data-testid="price-inbox">
          <h2 id="price-inbox" className="mb-2 text-lg font-semibold">
            {t('receipts.inbox.title')}
          </h2>
          <ul className="divide-y rounded-lg border text-sm">
            {inbox.data!.items.map((scan) => (
              <li
                key={scan.id}
                className="flex flex-wrap items-center justify-between gap-2 p-3"
                data-testid="price-inbox-item"
                data-status={scan.status}
              >
                <span className="min-w-0">
                  <span className="block font-medium">
                    {scan.productName ??
                      [scan.draft?.brand, scan.draft?.productName].filter(Boolean).join(' ') ??
                      t('receipts.inbox.unknownProduct')}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {scan.status === 'PROCESSING' && t('prices.tag.reading')}
                    {scan.status === 'FAILED' && describeWorkerError(scan.errorCode)}
                    {scan.status === 'READY' &&
                      scan.draft &&
                      formatMoney(scan.draft.price, scan.draft.currency ?? 'EUR')}
                  </span>
                </span>
                <span className="flex gap-2">
                  {scan.status === 'READY' && scan.draft && (
                    <Button
                      size="sm"
                      disabled={!(scan.productId ?? scan.draft.matchedProductId)}
                      title={
                        (scan.productId ?? scan.draft.matchedProductId)
                          ? undefined
                          : t('receipts.inbox.needsProduct')
                      }
                      onClick={() => setConfirming(scan)}
                      data-testid="price-inbox-confirm"
                    >
                      {t('receipts.inbox.check')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={dismiss.isPending && dismiss.variables === scan.id}
                    onClick={() => dismiss.mutate(scan.id)}
                    data-testid="price-inbox-dismiss"
                  >
                    {t('receipts.inbox.dismiss')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {receiptsEnabled && (
        <section aria-labelledby="receipts-list">
          <h2 id="receipts-list" className="mb-2 text-lg font-semibold">
            {t('receipts.listTitle')}
          </h2>
          {receipts.isLoading && <Spinner label={t('app.loading')} />}
          {receipts.data && receipts.data.items.length === 0 && (
            <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              <ReceiptText aria-hidden className="mx-auto mb-2 size-6" />
              {t('receipts.empty')}
            </p>
          )}
          {receipts.data && receipts.data.items.length > 0 && (
            <ul className="divide-y rounded-lg border" data-testid="receipt-list">
              {receipts.data.items.map((receipt) => (
                <ReceiptRow key={receipt.id} receipt={receipt} />
              ))}
            </ul>
          )}
        </section>
      )}

      {confirming?.draft && (
        <PriceDialog
          open
          onOpenChange={(next) => !next && setConfirming(null)}
          target={{
            productId: confirming.productId ?? confirming.draft.matchedProductId,
            name:
              confirming.productName ??
              [confirming.draft.brand, confirming.draft.productName].filter(Boolean).join(' '),
            packageQuantity: confirming.draft.packageQuantity,
            packageUnitCode: confirming.draft.packageUnitCode,
          }}
          defaultStoreId={confirming.storeId}
          scan={{ id: confirming.id, draft: confirming.draft }}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: receiptKeys.inbox })}
        />
      )}
    </PageContainer>
  );
}
